import { IProduct, StockStatus } from '@/context/ProductDataStore'
import { StockCheckCalculatedStock, StockCheckItem } from '@/services/api'

/**
 * Returns true if a product item can be purchased based on its stock status.
 * PREORDER is intentionally purchasable — only OUT_OF_STOCK or availableForSale:false blocks purchase.
 */
export const isPurchasableStatus = (
	status?: StockStatus,
	availableForSale?: boolean | null
): boolean => {
	if (availableForSale === false) return false
	return !status || status === 'IN_STOCK' || status === 'LOW_STOCK' || status === 'PREORDER'
}

/**
 * Merges a fresh `calculatedStock` response into a product item's `stockLimits`.
 * Only fields present in `calculatedStock` are overwritten; missing fields fall back
 * to whatever was already on the item. This is a pure function — safe to call outside
 * of React render (no hooks required).
 */
export const mergeCalculatedStock = (
	item: IProduct,
	calculatedStock?: StockCheckCalculatedStock
): IProduct => {
	if (!calculatedStock) return item

	const normalizedStock = {
		...calculatedStock,
		stockStatus: calculatedStock.stockStatus || (
			calculatedStock.isPreorder === true
				? 'PREORDER'
				: calculatedStock.availableForSale === false
					? 'OUT_OF_STOCK'
					: typeof calculatedStock.remainingStock === 'number'
						? (calculatedStock.remainingStock > 0 ? 'LOW_STOCK' : 'OUT_OF_STOCK')
						: 'IN_STOCK'
		)
	}

	const hasMaxDelay = normalizedStock.maxDelayDays !== undefined
	const hasRemainingStock = normalizedStock.remainingStock !== undefined
	const hasEstimatedDispatchDays = normalizedStock.estimatedDispatchDays !== undefined
	const hasStockMessage = normalizedStock.stockMessage !== undefined
	const hasIsPreorder = normalizedStock.isPreorder !== undefined
	const hasCanPreorder = normalizedStock.canPreorder !== undefined
	const hasOutOfStockSince = normalizedStock.outOfStockSince !== undefined
	const hasAvailableForSale = normalizedStock.availableForSale !== undefined
	const hasSharedPool = normalizedStock.sharedPool !== undefined

	return {
		...item,
		stockLimits: {
			...(item.stockLimits || {}),
			stockStatus: (normalizedStock.stockStatus as StockStatus) ?? item.stockLimits?.stockStatus,
			stockMessage: hasStockMessage ? normalizedStock.stockMessage ?? null : item.stockLimits?.stockMessage ?? null,
			isPreorder: hasIsPreorder ? normalizedStock.isPreorder ?? null : item.stockLimits?.isPreorder ?? null,
			canPreorder: hasCanPreorder ? normalizedStock.canPreorder ?? null : item.stockLimits?.canPreorder ?? null,
			outOfStockSince: hasOutOfStockSince ? normalizedStock.outOfStockSince ?? null : item.stockLimits?.outOfStockSince ?? null,
			availableForSale: hasAvailableForSale ? normalizedStock.availableForSale ?? null : item.stockLimits?.availableForSale ?? null,
			remainingStock: hasRemainingStock ? normalizedStock.remainingStock ?? null : item.stockLimits?.remainingStock ?? null,
			maxDelayDays: hasMaxDelay ? normalizedStock.maxDelayDays ?? null : item.stockLimits?.maxDelayDays ?? null,
			estimatedDispatchDays: hasEstimatedDispatchDays
				? normalizedStock.estimatedDispatchDays ?? null
				: item.stockLimits?.estimatedDispatchDays ?? null,
			additionalDispatchDays: hasMaxDelay
				? normalizedStock.maxDelayDays ?? null
				: item.stockLimits?.additionalDispatchDays ?? null,
			sharedPool: hasSharedPool
				? normalizedStock.sharedPool ?? null
				: item.stockLimits?.sharedPool ?? null,
		},
	}
}

/**
 * Builds a stock map and propagates PREORDER status across shared pools:
 * if any item in a pool is preorder, sibling items in that pool are also marked PREORDER
 * (unless explicitly unavailable/out-of-stock).
 */
export const buildStockMapWithSharedPoolPreorder = (
	items: StockCheckItem[],
	requestedCountsInput?: Map<string, number> | Record<string, number>
): Map<string, StockCheckCalculatedStock> => {
	const stockMap = new Map<string, StockCheckCalculatedStock>()
	items.forEach((item) => {
		stockMap.set(String(item.productItemId), item.calculatedStock || {})
	})

	const requestedCounts = new Map<string, number>()
	if (requestedCountsInput instanceof Map) {
		requestedCountsInput.forEach((count, id) => {
			requestedCounts.set(String(id), Number(count) || 0)
		})
	} else if (requestedCountsInput && typeof requestedCountsInput === 'object') {
		Object.entries(requestedCountsInput).forEach(([id, count]) => {
			requestedCounts.set(String(id), Number(count) || 0)
		})
	}

	const poolDefinitions = new Map<string, { members: Set<string>; preorderMessage: string | null }>()

	items.forEach((item) => {
		const current = item.calculatedStock || {}
		const sharedPool = current.sharedPool
		if (!sharedPool?.siblingProductItemIds?.length) {
			return
		}

		const members = new Set<string>([
			String(item.productItemId),
			...sharedPool.siblingProductItemIds.map(String),
		])
		const key = Array.from(members).sort().join('|')
		const existing = poolDefinitions.get(key)
		const nextMessage = current.stockMessage ?? null
		poolDefinitions.set(key, {
			members,
			preorderMessage: existing?.preorderMessage ?? nextMessage,
		})
	})

	poolDefinitions.forEach((pool) => {
		const hasPreorderInPool = Array.from(pool.members).some((id) => {
			const stock = stockMap.get(id)
			if (!stock) return false
			return stock.isPreorder === true || stock.stockStatus === 'PREORDER'
		})

		if (!hasPreorderInPool) {
			return
		}

		pool.members.forEach((id) => {
			const existing = stockMap.get(id)
			// Keep explicit out-of-stock signals as blockers.
			if (existing?.availableForSale === false || existing?.stockStatus === 'OUT_OF_STOCK') {
				return
			}

			stockMap.set(id, {
				...(existing || {}),
				stockStatus: 'PREORDER',
				isPreorder: true,
				availableForSale: true,
				stockMessage: existing?.stockMessage ?? pool.preorderMessage ?? null,
			})
		})
	})

	// If requested demand exceeds a shared pool and that pool supports preorder,
	// mark all requested members in that pool as preorder so basket/checkout badges align.
	const demandPools = new Map<string, { members: Set<string>; totalAvailable: number }>()
	stockMap.forEach((stock, id) => {
		const sharedPool = stock?.sharedPool
		if (!sharedPool || typeof sharedPool.totalAvailable !== 'number') {
			return
		}
		const members = new Set<string>([
			String(id),
			...((sharedPool.siblingProductItemIds || []).map(String)),
		])
		const key = Array.from(members).sort().join('|')
		if (!demandPools.has(key)) {
			demandPools.set(key, {
				members,
				totalAvailable: sharedPool.totalAvailable,
			})
		}
	})

	demandPools.forEach((pool) => {
		const totalRequested = Array.from(pool.members).reduce((sum, id) => {
			return sum + (requestedCounts.get(id) || 0)
		}, 0)

		if (totalRequested <= pool.totalAvailable) {
			return
		}

		const allowsPreorder = Array.from(pool.members).some((id) => {
			const stock = stockMap.get(id)
			return stock?.canPreorder === true || stock?.isPreorder === true || stock?.stockStatus === 'PREORDER'
		})

		if (!allowsPreorder) {
			return
		}

		pool.members.forEach((id) => {
			if ((requestedCounts.get(id) || 0) <= 0) {
				return
			}
			const existing = stockMap.get(id)
			if (existing?.availableForSale === false || existing?.stockStatus === 'OUT_OF_STOCK') {
				return
			}

			stockMap.set(id, {
				...(existing || {}),
				stockStatus: 'PREORDER',
				isPreorder: true,
				availableForSale: true,
			})
		})
	})

	return stockMap
}
